import React, {useState, useCallback, useEffect, useId} from 'react';
import {useNavigate} from 'react-router-dom';
import {CircularProgress} from '@mui/material';
import InputText from "../common/InputText";
import Button from "../common/Button";
import ReactDataGrid from '@inovua/reactdatagrid-community';
import '@inovua/reactdatagrid-community/index.css';
import {getIdMeBaseApiUrl} from "../../utils/UrlConstants";
import Dropdown from "../../utils/components/Dropdown";
import {useMsal} from '@azure/msal-react';
import {toast} from 'react-toastify';
import { displayPhoneNumber } from "../../utils/phoneUtils";
import { useIdentityAppService } from '../../services/IdentityServices';

// Interface for Microsoft Graph API user details
export interface UserDetail {
    id: string;
    displayName: string;
    givenName: string;
    surname: string;
    mobilePhone: string;
    businessPhones: string[];
    mail: string;
    userIdpGuid?: string;
    accountEnabled?: boolean;
    createdDateTime?: string;
    modifiedDateTime?: string;
}

// Interface for internal user (transformed from UserDetail)
interface InternalUser {
    userId: string;
    userLogin: string;
    userType: string;
    userEmail: string;
    userPhone: string;
    firstName: string;
    lastName: string;
    displayName: string;
    userIdpGuid?: string;
    userStatus?: string;
    createdDate?: string;
    modifiedDate?: string;
}

// Interface for processed user (includes processing status)
interface ProcessedUser extends InternalUser {
    microsoftGraphId?: string;
    existingUser?: boolean;
    processingError?: boolean;
    errorMessage?: string;
}

// Interface for search criteria
interface SearchCriteria {
    firstName: string;
    lastName: string;
    email: string;
}

// Filter criteria types
type FilterCriteria1 = 'firstName' | 'lastName' | 'fullName' | 'email' | '';
type FilterCriteria2 = 'firstName' | 'lastName' | 'fullName' | 'email' | '';

interface RequestHistoryResponse {
    success: boolean;
    message: string;
    requests: UserAppRequestDto[];
    totalCount: number;
    timestamp: string;
}

interface UserAppRequestDto {
    id: number;
    userId: number;
    userLogin: string;
    firstName: string;
    lastName: string;
    userEmail: string;
    applicationCode: string;
    applicationName: string;
    roleCode: string;
    roleName: string;
    assignedTo: string;
    status: string;
    operationalFlag: string;
    createdDate: string;
    modifiedDate: string;
    notes: string;
}

// Types for API responses
interface UserDto {
    userId: number;
    userLogin: string;
    userType: string;
    userEmail: string;
    userPhone: string;
    userIdpGuid: string;
    firstName: string;
    lastName: string;
    createdDate: string;
    modifiedDate: string;
    notes: string;
    userStatus: string;
}

interface UserSearchResponse {
    success: boolean;
    message: string;
    users: UserDto[];
    totalCount: number;
    timestamp: string;
}

interface ErrorResponse {
    success: boolean;
    error: string;
    details: string;
    statusCode: number;
    timestamp: string;
}

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(func: T, delay: number): T & { cancel: () => void } {
    let timeoutId: NodeJS.Timeout;

    const debouncedFunction = ((...args: Parameters<T>) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
    }) as T & { cancel: () => void };

    debouncedFunction.cancel = () => {
        clearTimeout(timeoutId);
    };

    return debouncedFunction;
}

// Interface for initial search state props
interface SearchStateProps {
    selectedOption1: string;
    selectedOption2: string;
    filter1Text: string;
    filter2Text: string;
    hasSearched: boolean;
    displayMode: 'requests' | 'search';
    searchResults: InternalUser[];
}

interface InternalUserManagementProps {
    initialSearchState?: SearchStateProps;
}

const InternalUserManagement: React.FC<InternalUserManagementProps> = ({ initialSearchState }) => {
    const navigate = useNavigate();
    const {accounts, instance} = useMsal();

    // Search form state
    const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>({
        firstName: '',
        lastName: '',
        email: ''
    });

    // Filter dropdown state - initialize from props if available
    const [selectedOption1, setSelectedOption1] = useState<FilterCriteria1>(
        (initialSearchState?.selectedOption1 as FilterCriteria1) || ''
    );
    const [selectedOption2, setSelectedOption2] = useState<FilterCriteria2>(
        (initialSearchState?.selectedOption2 as FilterCriteria2) || ''
    );
    const [filter1Text, setFilter1Text] = useState(initialSearchState?.filter1Text || '');
    const [filter2Text, setFilter2Text] = useState(initialSearchState?.filter2Text || '');

    // Search state - restore selectedUsers from props if available
    const [searchValue, setSearchValue] = useState('');
    const [userOptions, setUserOptions] = useState<UserDetail[]>([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState<InternalUser[]>(initialSearchState?.searchResults || []);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Users with requests state (from API) - restore from props if available
    const [usersWithRequests, setUsersWithRequests] = useState<UserDto[]>([]);
    const [initialLoadComplete, setInitialLoadComplete] = useState(initialSearchState?.searchResults ? true : false);
    const [noUsersFound, setNoUsersFound] = useState(false);
    const [displayMode, setDisplayMode] = useState<'requests' | 'search'>(initialSearchState?.displayMode || 'requests');
    const [hasSearched, setHasSearched] = useState(initialSearchState?.hasSearched || false);

    // Error state
    const [errors, setErrors] = useState({
        fullNameValidation: '',
        phoneValidation: ''
    });

    // Row selection state
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [selectedUserType, setSelectedUserType] = useState<string | null>(null);

    // User type validation state
    const [showUserTypePopup, setShowUserTypePopup] = useState(false);
    const [pendingUserId, setPendingUserId] = useState<string | null>(null);

    // Dropdown choices
    const searchDropDownChoices1 = [
        {label: 'First Name', value: 'firstName'},
        {label: 'Last Name', value: 'lastName'},
        {label: 'Full Name', value: 'fullName'},
        {label: 'Email Address', value: 'email'}
    ];

    const allSearchDropdownChoices = [
        {label: 'First Name', value: 'firstName'},
        {label: 'Last Name', value: 'lastName'},
        {label: 'Full Name', value: 'fullName'},
        {label: 'Email Address', value: 'email'}
    ];

    // Function to load users with any type of requests (Pending, Requested, Approved, Rejected, Revoked, etc.)
    const loadUsersWithRequests = async (): Promise<void> => {
        try {
            setLoading(true);
            setError(null);
            setNoUsersFound(false);
            setHasSearched(false);
            setDisplayMode('requests');

            // Get current user data from sessionStorage
            const currentUserDataStr = sessionStorage.getItem('currentUserData');
            if (!currentUserDataStr) {
                console.error('No current user data found in sessionStorage');
                setError('User session not found. Please log in again.');
                setUsersWithRequests([]);
                setLoading(false);
                setInitialLoadComplete(true);
                return;
            }

            let currentUserData;
            try {
                currentUserData = JSON.parse(currentUserDataStr);
            } catch (parseError) {
                console.error('Error parsing current user data:', parseError);
                setError('Invalid user session data. Please log in again.');
                setUsersWithRequests([]);
                setLoading(false);
                setInitialLoadComplete(true);
                return;
            }

            const response = await useIdentityAppService().postUserWithRequests(JSON.stringify({
                    user: currentUserData,
                    userType: 'Internal'
                })); 

            if (response.ok) {
                const data: UserSearchResponse = await response.json();
                setUsersWithRequests(data.users);
                setNoUsersFound(data.users.length === 0);
                console.log(`Loaded ${data.users.length} internal users with requests`);
            } else {
                const errorData: ErrorResponse = await response.json();
                setError(errorData.error || 'Failed to load users with requests');
                setUsersWithRequests([]);
            }
        } catch (err) {
            console.error('Error loading users with requests:', err);
            setError(err instanceof Error ? err.message : 'Network error occurred while loading users');
            setUsersWithRequests([]);
        } finally {
            setLoading(false);
            setInitialLoadComplete(true);
        }
    };

    // Load users with any type of requests when component mounts
    useEffect(() => {
        loadUsersWithRequests();
    }, []);

    // Function to get user details from Microsoft Graph API
    async function getUserDetailsByName(searchCriteria: SearchCriteria) {
        const account = accounts[0];

        const tokenRequest = {
            scopes: ['User.Read', 'Group.Read.All'],
            account: account
        };

        try {
            const response = await instance.acquireTokenSilent(tokenRequest);
            if (!response) {
                console.log("Failed to get access token");
                return;
            }

            const headers = new Headers();
            const accessToken = response.accessToken;
            const bearer = `Bearer ${accessToken}`;
            headers.append("Authorization", bearer);

            const options = {
                method: "GET",
                headers: headers
            };

            // Build filter query based on search criteria
            const filters = [];
            if (searchCriteria.firstName) {
                filters.push(`startsWith(givenName,'${searchCriteria.firstName}')`);
            }
            if (searchCriteria.lastName) {
                filters.push(`startsWith(surname,'${searchCriteria.lastName}')`);
            }
            if (searchCriteria.email) {
                filters.push(`startsWith(mail,'${searchCriteria.email}')`);
            }

            const filterQuery = filters.length > 0 ? filters.join(' or ') : "mail ne null";
            const url = `https://graph.microsoft.com/v1.0/users?$filter=${filterQuery}&$select=accountEnabled, createdDateTime, modifiedDateTime, givenName,surname,mail,mobilePhone,userPrincipalName,identities,displayname,id,extension_f95cb2dc0f0346e4b1b3d0ecf6b1b38b_userType,extension_f95cb2dc0f0346e4b1b3d0ecf6b1b38b_organizationName,extension_f95cb2dc0f0346e4b1b3d0ecf6b1b38b_partnerType,extension_f95cb2dc0f0346e4b1b3d0ecf6b1b38b_stateId`;

            const apiResponse = await useIdentityAppService().getApplicationEnvironmentsByIdpName(url)
            const data = await apiResponse.json();
            return data;
        } catch (error: any) {
            console.error(error);
            throw error;
        }
    }

    // Debounced function to fetch user details
    const fetchUserDetails = useCallback(debounce(async (name: string) => {
        if (!name) return;
        try {
            setLoading(true);
            setError(null);
            // Create simple search criteria for the old search functionality
            const simpleSearchCriteria: SearchCriteria = {
                firstName: '',
                lastName: '',
                email: name.includes('@') ? name : ''
            };
            
            // If it's not an email, treat it as a general name search
            if (!name.includes('@')) {
                simpleSearchCriteria.firstName = name;
                simpleSearchCriteria.lastName = name;
            }
            
            const response = await getUserDetailsByName(simpleSearchCriteria);
            const userDetails = response["@odata.context"] ? response.value : [];

            const transformedUserDetails = userDetails.map((user: UserDetail) => ({
                id: user.id,
                displayName: user.displayName,
                givenName: user.givenName,
                surname: user.surname,
                mobilePhone: user.mobilePhone,
                businessPhones: user.businessPhones,
                mail: user.mail,
                userIdpGuid: user.id,
                userStatus: user.accountEnabled === true ? 'Active' : 'Inactive',
                createdDate: user.createdDateTime,
                modifiedDate: user.modifiedDateTime
            }));

            if (transformedUserDetails.length > 0) {
                setIsDropdownOpen(true);
                setUserOptions(transformedUserDetails);
            } else {
                setUserOptions([]);
                setIsDropdownOpen(false);
            }
        } catch (error: any) {
            console.error('Failed to fetch user details:', error);
            setError('Failed to fetch user details');
            setUserOptions([]);
            setIsDropdownOpen(false);
        } finally {
            setLoading(false);
        }
    }, 500), [accounts, instance]);

    const debouncedFetchUserDetails = useCallback(fetchUserDetails, [fetchUserDetails]);

    // Function to validate full name format (FirstName LastName)
    const validateFullName = (fullName: string): boolean => {
        const trimmedName = fullName.trim();
        if (!trimmedName) return false;

        const nameParts = trimmedName.split(/\s+/);
        return nameParts.length >= 2 && nameParts.every(part => part.length > 0);
    };

    // Function to check if search is valid
    const isSearchValid = (): boolean => {
        const hasValidFilter1 = selectedOption1 && filter1Text.trim() !== '';
        const hasValidFilter2 = selectedOption2 && filter2Text.trim() !== '';

        // Check if at least one filter is selected and filled
        if (!(hasValidFilter1 || hasValidFilter2)) {
            return false;
        }

        // If Full Name is selected, validate the format
        if (selectedOption1 === 'fullName' && filter1Text.trim()) {
            if (!validateFullName(filter1Text)) {
                return false;
            }
        }

        // Check for any existing errors
        if (errors.fullNameValidation) {
            return false;
        }

        return true;
    };

    // Function to get available choices for Filter Criteria 2 (excluding Filter Criteria 1 selection)
    const getSearchDropdownChoices2 = () => {
        return allSearchDropdownChoices.filter(choice => choice.value !== selectedOption1);
    };

    // Function to handle filter 1 text change
    const handleFilter1TextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFilter1Text(value);

        // Validate full name format if Full Name is selected
        if (selectedOption1 === 'fullName') {
            if (value.trim() && !validateFullName(value)) {
                setErrors(prev => ({
                    ...prev,
                    fullNameValidation: 'Full name should be in format: FirstName LastName'
                }));
            } else {
                setErrors(prev => ({ ...prev, fullNameValidation: '' }));
            }
        }
    };

    // Updated filter 1 dropdown change handler
    const handleFilter1DropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value as FilterCriteria1;
        setSelectedOption1(value);
        setFilter1Text('');
        setErrors(prev => ({...prev, fullNameValidation: ''}));

        // Clear Filter 2 if it has the same value as Filter 1
        if (selectedOption2 === value) {
            setSelectedOption2('');
            setFilter2Text('');
        }
    };

    // Updated filter 2 dropdown change handler
    const handleFilter2DropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value as FilterCriteria2;
        setSelectedOption2(value);
        setFilter2Text('');
    };

    // Updated filter 2 text change handler to handle email validation
    const handleFilter2TextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFilter2Text(value);

        // Add email validation if needed for Filter 2
        if (selectedOption2 === 'fullName') {
            if (value.trim() && !validateFullName(value)) {
                setErrors(prev => ({
                    ...prev,
                    fullNameValidation: 'Full name should be in format: FirstName LastName'
                }));
            } else {
                setErrors(prev => ({...prev, fullNameValidation: ''}));
            }
        }
    };

    // Updated buildSearchCriteria function to handle both filters
    const buildSearchCriteria = (): SearchCriteria => {
        const criteria: SearchCriteria = {
            firstName: '',
            lastName: '',
            email: ''
        };

        // Handle Filter 1
        if (selectedOption1 && filter1Text.trim()) {
            switch (selectedOption1) {
                case 'firstName':
                    criteria.firstName = filter1Text.trim();
                    break;
                case 'lastName':
                    criteria.lastName = filter1Text.trim();
                    break;
                case 'fullName':
                    if (validateFullName(filter1Text)) {
                        const nameParts = filter1Text.trim().split(/\s+/);
                        criteria.firstName = nameParts[0];
                        criteria.lastName = nameParts.slice(1).join(' ');
                    }
                    break;
                case 'email':
                    criteria.email = filter1Text.trim();
                    break;
            }
        }

        // Handle Filter 2
        if (selectedOption2 && filter2Text.trim()) {
            switch (selectedOption2) {
                case 'firstName':
                    // Only set if not already set by Filter 1
                    if (!criteria.firstName) {
                        criteria.firstName = filter2Text.trim();
                    }
                    break;
                case 'lastName':
                    // Only set if not already set by Filter 1
                    if (!criteria.lastName) {
                        criteria.lastName = filter2Text.trim();
                    }
                    break;
                case 'fullName':
                    if (validateFullName(filter2Text)) {
                        const nameParts = filter2Text.trim().split(/\s+/);
                        // Only set if not already set by Filter 1
                        if (!criteria.firstName) {
                            criteria.firstName = nameParts[0];
                        }
                        if (!criteria.lastName) {
                            criteria.lastName = nameParts.slice(1).join(' ');
                        }
                    }
                    break;
                case 'email':
                    // Only set if not already set by Filter 1
                    if (!criteria.email) {
                        criteria.email = filter2Text.trim();
                    }
                    break;
            }
        }

        return criteria;
    };

    // Updated placeholder functions
    const getFilter1Placeholder = (): string => {
        switch (selectedOption1) {
            case 'firstName':
                return 'Enter first name';
            case 'lastName':
                return 'Enter last name';
            case 'fullName':
                return 'Enter full name (FirstName LastName)';
            case 'email':
                return 'Enter email address';
            default:
                return 'Select filter criteria first';
        }
    };

    const getFilter2Placeholder = (): string => {
        switch (selectedOption2) {
            case 'firstName':
                return 'Enter first name';
            case 'lastName':
                return 'Enter last name';
            case 'fullName':
                return 'Enter full name (FirstName LastName)';
            case 'email':
                return 'Enter email address';
            default:
                return 'Select filter criteria first';
        }
    };

    // Function to handle search button click
    const handleSearch = () => {
        if (isSearchValid()) {
            const searchCriteria = buildSearchCriteria();
            searchUsers(searchCriteria);
        }
    };

    // Function to search users using Microsoft Graph API
    const searchUsers = async (searchCriteria: SearchCriteria): Promise<void> => {
        try {
            setLoading(true);
            setError(null);
            setDisplayMode('search');
            setHasSearched(true);
            const response = await getUserDetailsByName(searchCriteria);
            const userDetails = response["@odata.context"] ? response.value : [];

            const transformedUserDetails = userDetails.map((user: UserDetail) => ({
                userId: user.id,
                userLogin: user.displayName,
                userType: 'Internal User',
                userEmail: user.mail,
                userPhone: user.mobilePhone || user.businessPhones?.[0] || '',
                firstName: user.givenName,
                lastName: user.surname,
                displayName: user.displayName,
                userIdpGuid: user.id,
                userStatus: user.accountEnabled === true ? 'Active' : 'Inactive',
                createdDate: user.createdDateTime,
                modifiedDate: user.modifiedDateTime

            }));

            setSelectedUsers(transformedUserDetails);
            setSelectedRows([]);
            setSelectedUserType(null);

            if (transformedUserDetails.length === 0) {
                setError('No users found matching your search criteria.');
            }
        } catch (error: any) {
            console.error('Failed to search users:', error);
            setError('Failed to search users');
            setSelectedUsers([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle search input change
    function handleSearchInputChange(event: React.ChangeEvent<HTMLInputElement>): void {
        const { value } = event.target;
        setSearchValue(value);

        if (!value.trim()) {
            debouncedFetchUserDetails.cancel();
            setIsDropdownOpen(false);
            setUserOptions([]);
        } else {
            debouncedFetchUserDetails(value.trim());
        }
    }

    // Handle user selection from dropdown
    const handleUserSelection = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedUserId = event.target.value;
        const selectedUser = userOptions.find(user => user.id === selectedUserId);

        if (selectedUser) {
            // Transform UserDetail to InternalUser format
            const internalUser: InternalUser = {
                userId: selectedUser.id,
                userLogin: selectedUser.displayName || '',
                userType: 'Internal',
                userEmail: selectedUser.mail || '',
                userPhone: selectedUser.mobilePhone || selectedUser.businessPhones?.[0] || '',
                firstName: selectedUser.givenName || '',
                lastName: selectedUser.surname || '',
                displayName: selectedUser.displayName || ''
            };

            // Check if user is already in the list
            const isUserAlreadySelected = selectedUsers.some(user => user.userId === selectedUser.id);

            if (!isUserAlreadySelected) {
                setSelectedUsers(prev => [...prev, internalUser]);
                setSelectedRows(prev => [...prev, selectedUser.id]);
                if (!selectedUserType) {
                    setSelectedUserType('Internal');
                }
            }

            setSearchValue(selectedUser.displayName || '');
            setIsDropdownOpen(false);
            setUserOptions([]);
        }
    };

    // Function to get the current data source based on display mode
    const getCurrentDataSource = (): any[] => {
        return displayMode === 'requests' ? usersWithRequests : selectedUsers;
    };

    // Function to get user type for a given user ID
    const getUserType = (userId: string | number): string | undefined => {
        const dataSource = getCurrentDataSource();
        const user = dataSource.find(user => String(user.userId) === String(userId));
        return user?.userType;
    };

    // Function to validate user type compatibility
    const validateUserTypeSelection = (newUserId: string | number): boolean => {
        if (selectedRows.length === 0) {
            return true; // First selection is always valid
        }

        const newUserType = getUserType(newUserId);
        if (!newUserType) return false;

        // If we already have a selected user type, check if the new one matches
        if (selectedUserType && newUserType !== selectedUserType) {
            return false;
        }

        // Check if all currently selected users have the same type as the new one
        const selectedUserTypes = selectedRows.map(userId => getUserType(userId));
        return selectedUserTypes.every(type => type === newUserType);
    };

    // Function to handle individual checkbox change with user type validation
    const handleCheckboxChange = (userId: string | number) => {
        const userIdStr = String(userId);
        const isCurrentlySelected = selectedRows.includes(userIdStr);

        if (isCurrentlySelected) {
            // Removing selection - always allowed
            const newSelectedRows = selectedRows.filter(id => id !== userIdStr);
            setSelectedRows(newSelectedRows);

            // Also remove from selectedUsers if in search mode
            if (displayMode === 'search') {
                setSelectedUsers(prev => prev.filter(user => String(user.userId) !== userIdStr));
            }

            // Update selected user type if no users are selected
            if (newSelectedRows.length === 0) {
                setSelectedUserType(null);
            }
        } else {
            // Adding selection - validate user type compatibility
            if (validateUserTypeSelection(userId)) {
                // Compatible user type, add to selection
                const newUserType = getUserType(userId);
                setSelectedRows(prevSelected => [...prevSelected, userIdStr]);
                if (!selectedUserType && newUserType) {
                    setSelectedUserType(newUserType);
                }
            } else {
                // Incompatible user type, show popup
                setPendingUserId(userIdStr);
                setShowUserTypePopup(true);
            }
        }
    };

    // Function to handle popup confirmation (Yes button)
    const handlePopupConfirm = () => {
        if (pendingUserId !== null) {
            const newUserType = getUserType(pendingUserId);
            // Clear previous selections and select only the new user
            setSelectedRows([pendingUserId]);
            setSelectedUserType(newUserType || null);
        }

        // Close popup and reset pending state
        setShowUserTypePopup(false);
        setPendingUserId(null);
    };

    // Function to handle popup cancellation (No button)
    const handlePopupCancel = () => {
        // Close popup and reset pending state without making changes
        setShowUserTypePopup(false);
        setPendingUserId(null);
    };

    // Function to handle "Select All" checkbox
    const handleSelectAllChange = (checked: boolean) => {
        const dataSource = getCurrentDataSource();
        if (checked) {
            // Select all users of the same type as the first user
            if (dataSource.length > 0) {
                const firstUserType = dataSource[0].userType;
                const sameTypeUsers = dataSource
                    .filter(user => user.userType === firstUserType)
                    .map(user => String(user.userId));
                setSelectedRows(sameTypeUsers);
                setSelectedUserType(firstUserType);
            }
        } else {
            // Deselect all
            setSelectedRows([]);
            setSelectedUserType(null);
        }
    };

    // Function to check if all visible rows are selected
    const areAllRowsSelected = (): boolean => {
        const dataSource = getCurrentDataSource();
        if (dataSource.length === 0) return false;

        // Only check users of the same type as currently selected
        if (!selectedUserType) return false;

        const sameTypeUsers = dataSource.filter(user => user.userType === selectedUserType);
        return sameTypeUsers.length > 0 && sameTypeUsers.every(user => selectedRows.includes(String(user.userId)));
    };

    // Function to check if some (but not all) rows are selected
    const areSomeRowsSelected = (): boolean => {
        return selectedRows.length > 0 && !areAllRowsSelected();
    };

    // Function to check if at least one row is selected
    const hasSelectedRows = (): boolean => {
        return selectedRows.length > 0;
    };

    // Function to check if exactly one row is selected
    const hasOneSelectedRow = (): boolean => {
        return selectedRows.length === 1;
    };

    // Function to handle "Manage User" button click
    const handleManageUser = async () => {
        if (selectedRows.length === 1) {
            const dataSource = getCurrentDataSource();
            const selectedUser = dataSource.find(user => String(user.userId) === selectedRows[0]);
            if (selectedUser) {
                console.log('Managing internal user:', selectedUser);

                try {
                    setLoading(true);

                    // Prepare user data for check-or-create-user API
                    const userRequest = {
                        userName: selectedUser.userLogin || selectedUser.displayName,
                        firstName: selectedUser.firstName,
                        lastName: selectedUser.lastName,
                        email: selectedUser.userEmail,
                        phone: selectedUser.userPhone || '',
                        userId: selectedUser.userId,
                        userType: 'Internal',
                        notes: `Internal user from Microsoft Graph: ${selectedUser.displayName}`
                    };

                    console.log('Checking/creating user:', userRequest);

                    // Call check-or-create-user API
                    const response = await useIdentityAppService().postUserCheckOrCreate(JSON.stringify(userRequest));

                    if (response.ok) {
                        const responseData = await response.json();
                        console.log('User check/creation response:', responseData);

                        if (responseData.success) {
                            // Build the user object with the database user ID
                            const processedUser: ProcessedUser = {
                                userId: responseData.userId.toString(),
                                userLogin: responseData.userLogin,
                                firstName: responseData.firstName,
                                lastName: responseData.lastName,
                                userEmail: responseData.userEmail,
                                userPhone: selectedUser.userPhone || '',
                                userType: responseData.userType,
                                displayName: selectedUser.displayName,
                                microsoftGraphId: selectedUser.userId,
                                existingUser: responseData.existingUser,
                                processingError: false,
                                userIdpGuid: selectedUser.userIdpGuid,
                                createdDate: selectedUser.createdDate,
                                modifiedDate: selectedUser.modifiedDate,
                                userStatus: selectedUser.userStatus
                            };

                            if (responseData.existingUser) {
                                console.log(`User ${selectedUser.displayName} already exists in database`);
                            } else {
                                console.log(`User ${selectedUser.displayName} created in database with ID: ${responseData.userId}`);
                            }

                            // Navigate to internal user details with the processed user data
                            navigate('/internal-user-details', {
                                state: {
                                    user: processedUser,
                                    userTypeSelection: 'Internal',
                                    searchState: {
                                        selectedOption1,
                                        selectedOption2,
                                        filter1Text,
                                        filter2Text,
                                        hasSearched,
                                        displayMode,
                                        searchResults: selectedUsers
                                    }
                                }
                            });
                        } else {
                            throw new Error(responseData.message || 'Failed to process user');
                        }
                    } else {
                        const errorData = await response.json();
                        console.error('Failed to check/create user:', errorData);
                        toast.error(`Failed to process user: ${errorData.message || 'Unknown error'}`);
                    }
                } catch (error: any) {
                    console.error('Error processing user:', error);
                    toast.error(`Error processing user: ${error?.message || 'Unknown error'}`);
                } finally {
                    setLoading(false);
                }
            }
        } else {
            toast.error('Please select exactly one user to manage');
        }
    };

    // Function to clear search and selections
    const handleClear = () => {
        console.log('Clear button clicked');
        // Clear dropdown filters
        setSelectedOption1('');
        setSelectedOption2('');
        setFilter1Text('');
        setFilter2Text('');
        setSearchCriteria({
            firstName: '',
            lastName: '',
            email: ''
        });
        // Clear original search
        setSearchValue('');
        setUserOptions([]);
        setIsDropdownOpen(false);
        setSelectedUsers([]);
        setSelectedRows([]);
        setSelectedUserType(null);
        setErrors({
            fullNameValidation: '',
            phoneValidation: ''
        });
        setError(null);
        setHasSearched(false);
        debouncedFetchUserDetails.cancel();

        // Reload users with requests
        loadUsersWithRequests();
    };

    // Function to handle "Assign Roles & Permissions" button click
    const handleAssignRoles = async () => {
        console.log('Assign Roles clicked for users:', selectedRows);

        // Get selected user details from the correct data source
        const dataSource = getCurrentDataSource();
        const selectedUserDetails = dataSource.filter(user => selectedRows.includes(String(user.userId)));
        console.log('Selected users details:', selectedUserDetails);
        console.log('Selected user type:', selectedUserType);

        // Check if the current user is trying to assign roles to themselves
        const currentUserName = accounts[0]?.name;
        if (currentUserName) {
            const selfAssignmentAttempt = selectedUserDetails.some(user =>
                user.displayName === currentUserName ||
                user.userLogin === currentUserName ||
                user.userEmail === accounts[0]?.username
            );

            if (selfAssignmentAttempt) {
                toast.error('You cannot assign roles to yourself. Please remove your name from the selection and try again.');
                //setError('Self-assignment not allowed: You cannot assign roles to yourself. Please unselect your name and proceed with role assignment for other users.');
                return;
            }
        }

        try {
            setLoading(true);
            setError(null);

            // Process each selected user through the check-or-create-user API
            const processedUsers: ProcessedUser[] = [];

            for (const user of selectedUserDetails) {
                try {
                    // Prepare user data for check-or-create-user API
                    const userRequest = {
                        userName: user.userLogin || user.displayName,
                        firstName: user.firstName,
                        lastName: user.lastName,
                        email: user.userEmail,
                        phone: user.userPhone || '',
                        userId: user.userId,
                        userType: 'Internal', // Set as internal user type
                        notes: `Internal user from Microsoft Graph: ${user.displayName}`
                    };

                    console.log('Checking/creating user:', userRequest);

                    // Call check-or-create-user API
                    const response = await useIdentityAppService().postUserCheckOrCreate(JSON.stringify(userRequest));

                    if (response.ok) {
                        const responseData = await response.json();
                        console.log('User check/creation response:', responseData);

                        if (responseData.success) {
                            // Add the processed user with the database user ID
                            const processedUser: ProcessedUser = {
                                userId: responseData.userId.toString(), // Use the database user ID
                                userLogin: responseData.userLogin,
                                firstName: responseData.firstName,
                                lastName: responseData.lastName,
                                userEmail: responseData.userEmail,
                                userPhone: user.userPhone || '',
                                userType: responseData.userType,
                                displayName: user.displayName,
                                microsoftGraphId: user.userId, // Keep original MS Graph ID for reference
                                existingUser: responseData.existingUser,
                                processingError: false
                            };

                            processedUsers.push(processedUser);

                            if (responseData.existingUser) {
                                console.log(`User ${user.displayName} already exists in database`);
                            } else {
                                console.log(`User ${user.displayName} created in database with ID: ${responseData.userId}`);
                            }
                        } else {
                            throw new Error(responseData.message || 'Failed to process user');
                        }
                    } else {
                        // Handle error case
                        const errorData = await response.json();
                        console.error('Failed to check/create user:', errorData);
                        throw new Error(errorData.message || 'API request failed');
                    }
                } catch (error: any) {
                    console.error('Error processing user:', error);
                    toast.error(`Error processing user ${user.displayName}: ${error?.message || 'Unknown error'}`);

                    // Add failed user to processed users with error flag
                    const failedUser: ProcessedUser = {
                        userId: user.userId,
                        userLogin: user.userLogin,
                        firstName: user.firstName,
                        lastName: user.lastName,
                        userEmail: user.userEmail,
                        userPhone: user.userPhone,
                        userType: 'internal',
                        displayName: user.displayName,
                        processingError: true,
                        errorMessage: error?.message || 'Network error'
                    };

                    processedUsers.push(failedUser);
                }
            }

            // Check if any users were successfully processed
            const successfulUsers = processedUsers.filter(user => !user.processingError);

            if (successfulUsers.length === 0) {
                toast.error('No users could be processed successfully. Please try again.');
                return;
            }

            if (successfulUsers.length < processedUsers.length) {
                toast.warning(`${successfulUsers.length} of ${processedUsers.length} users processed successfully. Proceeding with successful users only.`);
            } else {
                const existingCount = successfulUsers.filter(user => user.existingUser).length;
                const newCount = successfulUsers.length - existingCount;

                if (newCount > 0 && existingCount > 0) {
                    toast.success(`Processed ${successfulUsers.length} users: ${newCount} created, ${existingCount} already existed. Proceeding to role assignment.`);
                }/* else if (newCount > 0) {
                    toast.success(`Created ${newCount} new users successfully. Proceeding to role assignment.`);
                } else {
                    toast.success(`All ${existingCount} users already exist in database. Proceeding to role assignment.`);
                }*/
            }

            // Convert successful users to the format expected by role assignment
            const usersForRoleAssignment = successfulUsers.map(user => ({
                userId: user.userId,
                userLogin: user.userLogin,
                firstName: user.firstName,
                lastName: user.lastName,
                userEmail: user.userEmail,
                userPhone: user.userPhone,
                userType: user.userType,
                displayName: user.displayName
            }));

            // Navigate to role assignment page with processed users data
            navigate('/assign-roles', {
                state: {
                    selectedUsers: usersForRoleAssignment,
                    userType: 'Internal',
                    searchState: {
                        selectedOption1,
                        selectedOption2,
                        filter1Text,
                        filter2Text,
                        hasSearched,
                        displayMode,
                        searchResults: selectedUsers
                    }
                }
            });

        } catch (error: any) {
            console.error('Error in role assignment process:', error);
            setError('Failed to process users for role assignment: ' + (error?.message || 'Unknown error'));
            toast.error('Failed to process users for role assignment');
        } finally {
            setLoading(false);
        }
    };

    // Handle request history

    // Define grid columns for search results with proper multi-selection
    const userGridColumns = [
        {
            name: 'select',
            header: () => (
                <input
                    type="checkbox"
                    checked={areAllRowsSelected()}
                    ref={(input) => {
                        if (input) input.indeterminate = areSomeRowsSelected();
                    }}
                    onChange={(e) => handleSelectAllChange(e.target.checked)}
                    style={{cursor: 'pointer'}}
                />
            ),
            defaultWidth: 60,
            render: ({data}: any) => (
                <input
                    type="checkbox"
                    checked={selectedRows.includes(String(data.userId))}
                    onChange={() => handleCheckboxChange(data.userId)}
                    style={{cursor: 'pointer'}}
                />
            ),
            sortable: false,
            filterable: false
        },
        {
            name: 'userLogin',
            header: 'User Name',
            defaultFlex: 1,
            minWidth: 160,
        },
        {
            name: 'firstName',
            header: 'First Name',
            defaultFlex: 1,
            minWidth: 120,
            type: 'string',
        },
        {
            name: 'lastName',
            header: 'Last Name',
            defaultFlex: 1,
            minWidth: 120,
            type: 'string',
        },
        {
            name: 'userEmail',
            header: 'Email Address',
            defaultFlex: 2,
            minWidth: 200,
            type: 'string',
        },
        {
            name: 'userPhone',
            header: 'Phone Number',
            defaultFlex: 1,
            minWidth: 140,
            render: ({ value }: any) => displayPhoneNumber(value) || 'N/A',
        },
        {
            name: 'userType',
            header: 'User Type',
            defaultFlex: 1,
            minWidth: 120,
        }
    ];

    const filterValue = [
        {
            name: 'userEmail',
            operator: 'contains',
            type: 'string',
            value: '',
        },
        {
            name: 'userLogin',
            operator: 'contains',
            type: 'string',
            value: '',
        },
        {
            name: 'firstName',
            operator: 'contains',
            type: 'string',
            value: '',
        },
        {
            name: 'lastName',
            operator: 'contains',
            type: 'string',
            value: '',
        },
        {
            name: 'userType',
            operator: 'contains',
            type: 'string',
            value: '',
        },
        {
            name: 'userPhone',
            operator: 'contains',
            type: 'string',
            value: '',
        }
    ];

    // Function to get the grid title
    const getGridTitle = (): string => {
        if (loading) {
            return 'Loading...';
        }

        let title = '';
        const dataSource = displayMode === 'requests' ? usersWithRequests : selectedUsers;

        switch(displayMode) {
            case 'requests':
                title = `Users with Application Access (${dataSource.length} user${dataSource.length !== 1 ? 's' : ''})`;
                break;
            case 'search':
                title = `Search Results (${dataSource.length} user${dataSource.length !== 1 ? 's' : ''} found)`;
                break;
            default:
                title = `Users (${dataSource.length})`;
        }

        if (selectedUserType && selectedRows.length > 0) {
            return `${title} - Selected: ${selectedUserType} (${selectedRows.length})`;
        }
        return title;
    };

    return (
        <>
            {/* User Type Validation Popup */}
            {showUserTypePopup && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '8px',
                        maxWidth: '500px',
                        width: '90%',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                    }}>
                        <h3 style={{
                            marginTop: 0,
                            marginBottom: '20px',
                            color: '#333',
                            textAlign: 'center'
                        }}>
                            User Type Mismatch
                        </h3>
                        <p style={{
                            marginBottom: '25px',
                            lineHeight: '1.5',
                            color: '#555',
                            textAlign: 'center'
                        }}>
                            Only users with matching user types can be selected together for role assignment.
                            Your current selection will be cleared and replaced with this user.
                            Do you want to proceed?
                        </p>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '15px'
                        }}>
                            <Button
                                onClick={handlePopupConfirm}
                            >
                                Yes, Continue
                            </Button>
                            <Button
                                onClick={handlePopupCancel}
                            >
                                No, Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Search Filters Section with improved styling */}
            <div className={"external-filters"} style={{
                position: 'relative',
                zIndex: 10,
                pointerEvents: 'auto',
                marginTop: '60px'
            }}>
                {/* Filter Criteria 1 */}
                <div className={"external-filters-elem"} style={{
                    maxWidth: '220px',
                    position: 'relative',
                    zIndex: 11
                }}>
                    <Dropdown
                        choices={searchDropDownChoices1}
                        emptyChoiceLabel={"Select Filter Criteria 1"}
                        label={""}
                        name={"filterCriteria1"}
                        value={selectedOption1}
                        onChange={handleFilter1DropdownChange}
                    />
                </div>
                <div className="external-filters-elem" style={{
                    maxWidth: '250px',
                    position: 'relative',
                    zIndex: 11
                }}>
                    <InputText
                        context="registration"
                        name="filter1"
                        placeholder={getFilter1Placeholder()}
                        value={filter1Text}
                        onChange={handleFilter1TextChange}
                        small={true}
                        readOnly={!selectedOption1}
                    />
                    {errors.fullNameValidation && (
                        <div style={{color: "red", marginTop: "5px"}}>
                            {errors.fullNameValidation}
                        </div>
                    )}
                </div>

                {/* Filter Criteria 2 */}
                <div className="external-filters-elem" style={{
                    maxWidth: '220px',
                    position: 'relative',
                    zIndex: 11
                }}>
                    <Dropdown
                        choices={getSearchDropdownChoices2()}
                        emptyChoiceLabel="Select Filter Criteria 2"
                        name="filterCriteria2"
                        label=""
                        value={selectedOption2}
                        onChange={handleFilter2DropdownChange}
                    />
                </div>
                <div className="external-filters-elem" style={{
                    maxWidth: '250px',
                    position: 'relative',
                    zIndex: 11
                }}>
                    <InputText
                        context="registration"
                        name="filter2"
                        placeholder={getFilter2Placeholder()}
                        value={filter2Text}
                        small={true}
                        onChange={handleFilter2TextChange}
                        readOnly={!selectedOption2}
                    />
                </div>

                {/* Action Buttons with improved styling */}
                <div className="external-filters-action external-filters-action-right" style={{
                    position: 'relative',
                    zIndex: 11,
                    pointerEvents: 'auto'
                }}>
                    <Button
                        disabled={!(
                            (selectedOption1 && filter1Text.trim()) ||
                            (selectedOption2 && filter2Text.trim())
                        )}
                        className="OkButton"
                        onClick={handleClear}
                    >
                        Clear
                    </Button>
                    <Button
                        disabled={!isSearchValid() || loading}
                        className="OkButton"
                        onClick={handleSearch}
                    >
                        {loading ? 'Searching...' : 'Search'}
                    </Button>
                    <Button
                        className="OkButton"
                        disabled={!hasSelectedRows() || loading}
                        onClick={handleAssignRoles}
                    >
                        {loading ? 'Processing Users...' : 'Assign Roles & Permissions'}
                    </Button>
                    <Button
                        className="OkButton"
                        disabled={!hasOneSelectedRow() || loading}
                        onClick={handleManageUser}
                    >
                        Manage User
                    </Button>
                    <Button
                        className="OkButton"
                        onClick={() => navigate('/request-history', { state: { userType: 'Internal', searchState: { selectedOption1, selectedOption2, filter1Text, filter2Text, hasSearched, displayMode, searchResults: selectedUsers } } })}
                        disabled={loading}
                    >
                        {loading ? 'Loading...' : 'Request History'}
                    </Button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div style={{
                    marginTop: '20px',
                    padding: '16px',
                    color: '#c62828'
                }}>
                    <strong>Error:</strong> {error}
                </div>
            )}

            {/* Loading Message */}
            {loading && !initialLoadComplete && (
                <div style={{
                    marginTop: '30px',
                    textAlign: 'center',
                    padding: '20px'
                }}>
                    <CircularProgress size={24} style={{marginRight: '10px'}}/>
                    Loading users...
                </div>
            )}

            {/* Users Grid */}
            {(initialLoadComplete || hasSearched) && (
                <div style={{marginTop: '30px'}}>
                    <h4 style={{marginBottom: '20px'}}>
                        {getGridTitle()}
                    </h4>
                    {(() => {
                        const dataSource = displayMode === 'requests' ? usersWithRequests : selectedUsers;
                        return dataSource.length > 0 ? (
                            <ReactDataGrid
                                key={`${displayMode}-${dataSource.length}`}
                                licenseKey="AppName=CoherenceApp,Company=Coherence,ExpiryDate=2025-04-15T00:00:00.000Z,LicenseDeveloperCount=1,LicenseType=single_app,Ref=CoherenceLicenseRef,Z=983320388-1513273079-1127323721-2081795733-199375339958733905"
                                idProperty="userId"
                                pagination
                                columns={userGridColumns}
                                dataSource={dataSource}
                                sortable={true}
                                defaultLimit={10}
                                rowHeight={50}
                                className="pointer-cursor-row"
                                enableColumnAutosize={true}
                                style={{minHeight: 400}}
                                defaultFilterValue={filterValue}
                            />
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                padding: '40px',
                                color: '#666',
                                fontSize: '16px'
                            }}>
                                {hasSearched
                                    ? 'No users found matching your search criteria.'
                                    : 'No internal users with requests found. Use the search filters above to find and manage internal users.'}
                            </div>
                        );
                    })()}
                </div>
            )}
        </>
    );
};

export default InternalUserManagement;